import type { FullGestureState } from '@use-gesture/react'
import { type RefObject, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { GlobalActions } from '@/store'
import { isInsideOwnWheel } from '@/utils'
import { getCanvasState } from '../../helpers/canvas'
import { useCanvas } from './useCanvas'
import useScaleEvents from './useScaleEvents'
import { useWheelZoom } from './useWheelZoom'

type Event = Omit<FullGestureState<'wheel'>, 'event'> & { event: WheelEvent }

type UseWheelEvents = {
  onWheel: (event: Event, ref: RefObject<any>) => void
}

const useWheelEvents = (): UseWheelEvents => {
  const dispatchCanvasUpdate = useCanvas()
  const dispatch = useDispatch()
  const { onWheelZoom } = useScaleEvents()
  const zoomByWheel = useWheelZoom(onWheelZoom)
  const panFrame = useRef<number | null>(null)
  const panDelta = useRef<[number, number]>([0, 0])

  const cancelPan = () => {
    if (panFrame.current !== null) {
      cancelAnimationFrame(panFrame.current)
    }
    panFrame.current = null
    panDelta.current = [0, 0]
  }

  useEffect(() => cancelPan, [])

  const onWheel = (event: Event, ref: RefObject<any>) => {
    // Cmd+wheel zooms, as in Figma (OCT-1834). Ctrl+wheel and a trackpad
    // pinch come with ctrlKey and are zoomed by usePinchEvents instead.
    const zoom = event.metaKey && !event.ctrlKey

    if (zoom || event.ctrlKey) {
      cancelPan()
    }

    // scrolls the element under the pointer, see isInsideOwnWheel; a zoom,
    // like a pinch, zooms the canvas wherever the pointer is
    if (!zoom && isInsideOwnWheel(event.event.target)) {
      return
    }

    // Once per gesture, not per tick: a wheel fires dozens of events a second
    // and every store dispatch wakes up every selector.
    if (event.first) {
      dispatch(GlobalActions.cancelJump())
    }

    if (zoom) {
      if (!event.last) {
        const { deltaY, deltaMode } = event.event
        // Wheel deltas can be expressed in pixels, lines, or pages.
        const unit =
          deltaMode === 1 ? 16 : deltaMode === 2 ? window.innerHeight : 1

        zoomByWheel(deltaY * unit, [event.event.clientX, event.event.clientY])
      }

      return
    }

    if (!event.last && !event.ctrlKey && ref?.current) {
      const { delta } = event
      panDelta.current = [
        panDelta.current[0] + delta[0],
        panDelta.current[1] + delta[1],
      ]
      if (panFrame.current !== null) {
        return
      }

      panFrame.current = requestAnimationFrame(() => {
        // Read at application time: a zoom may have moved the canvas since
        // the wheel event. Panning must never restore an earlier scale.
        const { translate } = getCanvasState(ref.current)
        const [dx, dy] = panDelta.current
        panFrame.current = null
        panDelta.current = [0, 0]
        dispatchCanvasUpdate({
          translate: [translate[0] - dx, translate[1] - dy],
        })
      })
    }
  }

  return { onWheel }
}

export default useWheelEvents
